'use client';

import { Button, Icon, Loader, Logo } from '../../components';
import { Paper } from '../../layout';
import type { LinkItem } from '../../types';

export type VerifyEmailStatus = 'loading' | 'success' | 'alreadyVerified' | 'error';

export interface VerifyEmailProps {
  title: React.ReactNode;
  status: VerifyEmailStatus;
  /** Message to display for success state */
  successMessage?: string;
  /** Message to display for already verified state */
  alreadyVerifiedMessage?: string;
  /** Message to display for error state */
  errorMessage?: string;
  /** Loading text */
  loadingText?: string;
  /** Sign in button text */
  signInButtonText?: string;
  /** Back to sign in link */
  backToSignInLink?: LinkItem;
  /** Show links */
  links?: boolean;
  /** Called when sign in button is clicked */
  onSignIn?: () => void;
}

export function VerifyEmail(props: VerifyEmailProps) {
  const {
    title,
    status,
    successMessage = 'Email verified successfully. You can now sign in to your account.',
    alreadyVerifiedMessage = 'Email is already verified. You can sign in to your account.',
    errorMessage = 'An error occurred',
    loadingText = 'Verifying your email...',
    signInButtonText = 'Sign In',
    backToSignInLink,
    links = false,
    onSignIn
  } = props;

  return (
    <div className='flex flex-col items-center gap-xl w-full max-w-md px-md sm:px-0'>
      <Logo className='h-10' />
      <Paper className='flex flex-col items-center gap-sm shadow-lg border border-border-base rounded-xl w-full p-lg sm:p-xl'>
        <Paper.Title>{title}</Paper.Title>
        <Paper.Content className='w-full flex flex-col items-center'>
          {status === 'loading' && (
            <div className='text-center py-md'>
              <div className='flex justify-center mb-md'>
                <Loader />
              </div>
              <p className='text-foreground-muted'>{loadingText}</p>
            </div>
          )}

          {(status === 'success' || status === 'alreadyVerified') && (
            <div className='text-center py-sm'>
              <div className='mx-auto flex items-center justify-center h-15 w-15 rounded-full bg-success-solid mb-md'>
                <Icon
                  name='check'
                  className='h-9 w-9 text-white stroke-current [stroke-width:32] [stroke-linejoin:round] [stroke-linecap:round]'
                />
              </div>
              <p className='text-foreground mb-lg'>
                {status === 'alreadyVerified' ? alreadyVerifiedMessage : successMessage}
              </p>
              <Button size='full' className='w-full!' onClick={onSignIn}>
                {signInButtonText}
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className='text-center py-md'>
              <div className='mx-auto flex items-center justify-center h-15 w-15 rounded-full bg-error-bg mb-md'>
                <Icon name='xmark' className='h-9 w-9 text-error-text' />
              </div>
              <p className='text-error-text mb-lg'>{errorMessage}</p>
              {links && backToSignInLink && (
                <a
                  href={backToSignInLink.href}
                  className='text-primary-base hover:text-primary-hover flex items-center justify-center gap-xs'
                >
                  <Icon name='chevron-down' rotate={90} className='text-primary-base' />
                  {backToSignInLink.label}
                </a>
              )}
            </div>
          )}
        </Paper.Content>
      </Paper>
    </div>
  );
}
